// Copyright 2026, Imprivata, Inc.  All rights reserved.

import type { AppProfile } from '@api/types';
import SearchInput from '@components/searchInput/SearchInput';
import { useErrorContext } from '@errorHandler/context/ErrorContext';
import { AppError } from '@errorHandler/errors';
import { Button } from '@imprivata-cloud/components';
import { Spin, Table } from 'antd';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { buildAppProfilesColumns, buildAppProfilesRowSelection } from './AppProfilesColumns';
import { TRANSLATION_ROOT } from './AppProfiles.types';
import { AppProfilesImportButton } from './AppProfilesImportButton';
import { AppProfilesDeleteModal, AppProfilesEmptyState } from './AppProfilesModals';
import { useAppProfilesQuery, useDeleteAppProfilesMutation } from './useAppProfiles';

export const AppProfiles = () => {
  const { t } = useTranslation();
  const { emitError } = useErrorContext();
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  const { data, isLoading, error } = useAppProfilesQuery();
  const { mutate: deleteAppProfiles, isPending: isDeleting } = useDeleteAppProfilesMutation();

  useEffect(() => {
    if (error) {
      emitError(new AppError(error));
    }
  }, [error, emitError]);

  const appProfiles = data?.appProfiles ?? [];

  // Client-side text search over displayName/machineName only — the API has
  // no server-side filter and the full list is already in memory, so no
  // debounce is needed here (unlike Endpoints.tsx's server-paged search).
  const filteredAppProfiles = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();
    if (!query) {
      return appProfiles;
    }
    return appProfiles.filter(
      profile =>
        profile.displayName.toLowerCase().includes(query) ||
        profile.machineName.toLowerCase().includes(query),
    );
  }, [appProfiles, searchQuery]);

  const confirmDelete = () => {
    deleteAppProfiles(
      { appProfileIds: selectedIds },
      {
        onSuccess: () => setSelectedIds([]),
        onError: e => emitError(new AppError(e)),
        onSettled: () => setIsDeleteModalOpen(false),
      },
    );
  };

  const renderBody = () => {
    if (isLoading) {
      return (
        <div className="applications-loading">
          <Spin />
          <span>{t(`${TRANSLATION_ROOT}.loading`)}</span>
        </div>
      );
    }
    if (!error && appProfiles.length === 0) {
      return <AppProfilesEmptyState />;
    }
    return (
      <>
        <div className="app-profiles-toolbar">
          <SearchInput
            placeholder={t(`${TRANSLATION_ROOT}.search-placeholder`)}
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
          />
          <div className="app-profiles-toolbar-actions">
            <AppProfilesImportButton />
            <Button
              type="default"
              label={t(`${TRANSLATION_ROOT}.delete-cta`)}
              disabled={selectedIds.length === 0}
              onClick={() => setIsDeleteModalOpen(true)}
            />
          </div>
        </div>
        <Table<AppProfile>
          dataSource={filteredAppProfiles}
          rowKey="appProfileId"
          pagination={false}
          rowSelection={buildAppProfilesRowSelection(selectedIds, setSelectedIds)}
          columns={buildAppProfilesColumns(t)}
        />
      </>
    );
  };

  return (
    <div className="applications-container app-profiles-container">
      <h2>{t(`${TRANSLATION_ROOT}.heading`)}</h2>
      {renderBody()}
      <AppProfilesDeleteModal
        open={isDeleteModalOpen}
        selectedCount={selectedIds.length}
        isLoading={isDeleting}
        onConfirm={confirmDelete}
        onCancel={() => setIsDeleteModalOpen(false)}
      />
    </div>
  );
};

export default AppProfiles;
